// Iter-408 — Vertical-aware copy for PartnerRateTable surfaces.
//
// Extracted from PartnerRateTable.jsx to keep that file under the 3400-
// line iter-276g modularization budget AND to centralise all per-vertical
// terminology in one place. When a new vertical lands, edit one file.
//
// Categories: insurance, warranty, auto-loan-new, auto-loan-used,
// refinance. The fallbacks below cover any unknown future category so
// the UI never reads "undefined".

// ──────── Compare-stash coachmark tooltip ────────
// Shown once per visitor on the first row of the rate table. Vertical-
// aware copy ensures the noun ("carriers" vs "lenders" vs "providers")
// and comparison dimensions ("premiums, coverage, ratings" vs "APR,
// terms, scores") feel native on every chart.
const COMPARE_HINT_COPY_BY_CATEGORY = {
  insurance:        "Stack 2–4 carriers side-by-side to compare premiums, coverage, and ratings at once.",
  warranty:         "Stack 2–4 providers side-by-side to compare price, coverage, and ratings at once.",
  "auto-loan-new":  "Stack 2–4 lenders side-by-side to compare APR, terms, and scores at once.",
  "auto-loan-used": "Stack 2–4 lenders side-by-side to compare APR, terms, and scores at once.",
  refinance:        "Stack 2–4 refi lenders side-by-side to compare APR, terms, and scores at once.",
};
const COMPARE_HINT_FALLBACK =
  "Stack 2–4 options side-by-side to compare pricing, terms, and ratings at once.";
export function getCompareHintCopy(category) {
  return COMPARE_HINT_COPY_BY_CATEGORY[category] || COMPARE_HINT_FALLBACK;
}

// ──────── "Comparing N {audited noun}" trust caption ────────
// Caption shape stays consistent across every vertical — previously
// only insurance + warranty got the "audited" qualifier and loan rows
// dropped it (asymmetric, off-brand). The map below normalises it.
const AUDITED_NOUN_BY_CATEGORY = {
  insurance:        "audited carriers",
  warranty:         "audited providers",
  "auto-loan-new":  "audited lenders",
  "auto-loan-used": "audited lenders",
  refinance:        "audited refi lenders",
};
export function getAuditedNoun(category) {
  return AUDITED_NOUN_BY_CATEGORY[category] || "audited options";
}

// ──────── "{Verified noun} verified {date}" provenance pair ────────
// Insurance is about premiums; warranty is about prices; loan/refi
// products are about rates. Single-source helper avoids the brittle
// inline ternary that previously labelled warranty rows "Rates".
const VERIFIED_NOUN_BY_CATEGORY = {
  insurance: "Premiums",
  warranty:  "Prices",
};
export function getVerifiedNoun(category) {
  return VERIFIED_NOUN_BY_CATEGORY[category] || "Rates";
}

// ──────── Empty-state filter hint ────────
// Shown when active user filters wipe the result list to zero. Insurance
// + warranty don't expose a credit-score filter, so the loan-default
// "widen the credit-score range" advice was misleading. Each vertical
// points the user at filters that actually exist on its chart.
const EMPTY_STATE_HINT_BY_CATEGORY = {
  insurance:
    "Try widening the premium range, dropping the minimum rating, or clearing the highlight chip to see the full editor-ranked list.",
  warranty:
    "Try dropping the minimum rating or clearing the highlight chip to see the full editor-ranked list of providers.",
};
const EMPTY_STATE_HINT_FALLBACK =
  "Try widening the credit-score range, dropping the minimum rating, or clearing the highlight chip to see the full editor-ranked list.";
export function getEmptyStateHintCopy(category) {
  return EMPTY_STATE_HINT_BY_CATEGORY[category] || EMPTY_STATE_HINT_FALLBACK;
}

// localStorage key for "first-scroll" coachmark dismissal — kept here
// so the storage key + the copy that reads from it live side by side.
export const COMPARE_HINT_STORAGE_KEY = "carsavr:compare-hint-dismissed";
